import {
  currentModuleOf,
  pathOrderedActivities,
  statusOf,
  moduleView,
  type ActivityStatus,
} from '@/shared/program/derive';
import { pathRoleOf, type PathRole } from '@/shared/program/path-label';
import { useProgramFull } from '@/shared/program/use-program-full';

import type { ActivityKind, FullActivity, ProgramFull } from '@mio/shared';

const STUDIO_KINDS = new Set<ActivityKind>(['lesson', 'workshop', 'checkpoint']);

export interface StudioItem extends FullActivity {
  status: ActivityStatus;
  moduleNumber: number;
}

export interface StudioModule {
  number: number;
  durationDays: number;
  title: string;
  outcome: string;
  whyThisModule: string | null;
  /** Who the module is for, from the content; null when the module has no placement text. */
  placement: ProgramFull['modules'][number]['placement'];
  fieldThread: string;
  mioStance: string;
  readings: ProgramFull['modules'][number]['readings'];
  status: 'current' | 'complete' | 'upcoming';
  /** The module's place on the learner's saved path; null without a path. */
  path: PathRole | null;
  items: StudioItem[];
  workshops: StudioItem[];
  done: number;
}

export interface StudioModel {
  /** Resume records are validated against the currently authorized program. */
  programSlug?: string;
  currentModule: number;
  totalModules: number;
  modules: StudioModule[];
  /** The learner's saved path from the placement questionnaire; null until they answer it. */
  path: ProgramFull['path'];
  /** The first unfinished lesson, workshop, or checkpoint, searching the learner's path first. */
  continueAt: StudioItem | null;
  /** All Studio items in order, for keyboard and next/previous navigation. */
  sequence: StudioItem[];
  done: number;
  total: number;
}

function buildStudio(program: ProgramFull): StudioModel {
  const currentModule = currentModuleOf(program);
  const modules: StudioModule[] = program.modules.map((module) => {
    const view = moduleView(program, module.number);
    const withStatus = module.activities.map<StudioItem>((a) => ({
      ...a,
      status: statusOf(program, a.id),
      moduleNumber: module.number,
    }));
    const items = withStatus.filter((a) => STUDIO_KINDS.has(a.kind));
    return {
      number: module.number,
      durationDays: module.durationDays,
      title: module.title,
      outcome: module.outcome,
      whyThisModule: module.whyThisModule,
      placement: module.placement,
      fieldThread: module.fieldThread,
      mioStance: module.mioStance,
      readings: module.readings,
      status: view?.status ?? 'upcoming',
      path: pathRoleOf(program.path, module.number),
      items,
      workshops: withStatus.filter((a) => a.kind === 'workshop'),
      done: items.filter((a) => a.status === 'complete').length,
    };
  });
  const sequence = modules.flatMap((w) => w.items);
  const studioIds = new Set(sequence.map((item) => item.id));
  const nextOnPath = pathOrderedActivities(program).find(
    (a) => studioIds.has(a.id) && statusOf(program, a.id) !== 'complete',
  );
  return {
    programSlug: program.slug,
    currentModule,
    totalModules: program.modules.length,
    modules,
    path: program.path,
    continueAt: sequence.find((item) => item.id === nextOnPath?.id) ?? null,
    sequence,
    done: sequence.filter((a) => a.status === 'complete').length,
    total: sequence.length,
  };
}

export function useStudio() {
  return useProgramFull(buildStudio);
}
